import { download, getPlaylistByUri, getFileWithUri, getFolderWithUri } from '../digitalVault';
import { getSharedCollectionById } from '../collectionManager';
/**
 * These are the scenarios for downloads of shared content:
 *
 *     Single file
 *         From resources page - selected one file resource - use location prop and file/sharedContent
 *         From detail view - use uri prop and file/sharedContent
 *         From clicked into one photo album/music playlist view - selected one file - use uri prop and file/sharedContent
 *         From clicked into one folder - selected one file - use uri prop and file/sharedContent
 *         From one collection - selected one file - use uri prop and file/sharedContent
 *     Single folder
 *         From resources page - selected one folder resource - use location prop and operations/zip/sharedContent
 *         From clicked into one folder view - selected one folder or main button on top - use uri prop and operations/zip/sharedContent
 *     Mixed files and folders selection
 *         From resources page - selected multiple file and/or folders resources - use location props and operations/zip/sharedContent with multiple uris in one api call
 *         From clicked into one folder view - selected multiple file and/or folders - use uri props and operations/zip/sharedContent with multiple uris in one api call
 *         From one collection view - selected multiple files or currently download of complete collection - use uri props and operations/zip/sharedContent with multiple uris in one api call
 *     One playlist (photo album/music playlist)
 *         From resources page - selected one playlist resource - use location prop and operations/zip/sharedContent
 *
 *     Unsupported:
 *     Multiple playlists (photo album/music playlist) - This is the one I mentioned we should try and see if API supports multiple playlist uris
 *         From resources page - selected multiple playlist resources - use location props and operations/zip/sharedContent with multiple uris in one api call
 *
 *     All these need to work except last one that one is to be confirmed if API supports it
 *
 *     http://mavenrepo.synchronoss.net:8081/nexus/content/sites/sites/com/newbay/vault/5.4.0/htmlsingle/vault-dataapi/#dv.operations.zip.shared
 *     http://mavenrepo.synchronoss.net:8081/nexus/content/sites/sites/com/newbay/vault/5.4.0/htmlsingle/vault-dataapi/#dv.file.shared.content.get
 */
const SHARE_FILE = 'dv-file-share';
const SHARE_FOLDER = 'dv-folder-share';
const SHARE_PLAYLIST = 'dv-playlist-share';
const SHARE_PLAYLIST_FILE = 'dv-playlist-share-file';
const SHARE_COLLECTION_ITEM = 'dv-shared-collection-item';
const ENDPOINT_ZIP = 'operations/zip/sharedContent';
const ENDPOINT_FILE = 'file/sharedContent';
const getUri = (resource) => {
    const { location, uri } = resource;
    return location || uri;
};
const getShareProps = (resource) => {
    const uri = getUri(resource);
    if (uri.includes(SHARE_FILE)) {
        return { shareType: SHARE_FILE, uri };
    }
    if (uri.includes(SHARE_FOLDER)) {
        return { shareType: SHARE_FOLDER, uri };
    }
    if (uri.includes(SHARE_PLAYLIST_FILE)) {
        return { shareType: SHARE_PLAYLIST_FILE, uri };
    }
    if (uri.includes(SHARE_PLAYLIST)) {
        return { shareType: SHARE_PLAYLIST, uri };
    }
    if (uri.includes(SHARE_COLLECTION_ITEM)) {
        return { shareType: SHARE_COLLECTION_ITEM, uri };
    }
    return null;
};
// for single files: before we invoke download check with
// dv.file.get or dv.folder.get or dv.playlist.metadata
const checkShare = async (shareType, uri) => {
    // check file-shares
    if (shareType === SHARE_FILE || shareType === SHARE_PLAYLIST_FILE) {
        await getFileWithUri(uri);
    }
    // check folder-shares
    if (shareType === SHARE_FOLDER) {
        await getFolderWithUri(uri, { count: 1 });
    }
    // check playlist-shares
    if (shareType === SHARE_PLAYLIST) {
        await getPlaylistByUri(uri);
    }
};
const validDownloadRequest = async (req) => {
    if (req.uris.length === 1) {
        const { shareType, uri } = getShareProps({ uri: req.uris[0] });
        await checkShare(shareType, encodeURIComponent(uri));
    }
    return true;
};
const downloadShare = async (props) => {
    // return if there are no files
    if (!props || !props.files || !props.files.length) {
        return;
    }
    const { files, token, host, name, checkResource, postReqDownload = false, logger } = props;
    let downloadRequest = {
        token,
        host,
        name,
        uris: [],
        method: postReqDownload ? 'POST' : 'GET',
        logger,
    };
    const updateDownloadRequest = (update) => {
        downloadRequest = {
            ...downloadRequest,
            ...update,
        };
    };
    // handle share
    const handleShareProps = (prop) => {
        const { shareType, uri } = prop;
        // handle file-shares
        if (shareType === SHARE_FILE ||
            shareType === SHARE_PLAYLIST_FILE ||
            shareType === SHARE_COLLECTION_ITEM) {
            if (downloadRequest.uris.length) {
                updateDownloadRequest({
                    uris: [...downloadRequest.uris, uri],
                    endpoint: ENDPOINT_ZIP,
                });
            }
            else {
                updateDownloadRequest({
                    uris: [uri],
                    endpoint: ENDPOINT_FILE,
                });
            }
        }
        // handle folder-shares
        if (shareType === SHARE_FOLDER) {
            updateDownloadRequest({
                uris: [...downloadRequest.uris, uri],
                endpoint: ENDPOINT_ZIP,
            });
        }
        // handle playlist-shares
        if (shareType === SHARE_PLAYLIST) {
            updateDownloadRequest({
                uris: [...downloadRequest.uris, uri],
                endpoint: ENDPOINT_ZIP,
            });
        }
    };
    // iterate over files and build download list
    [...files].forEach((file) => {
        const shareProps = getShareProps(file);
        shareProps && handleShareProps(shareProps);
    });
    if (files.length === 1 && files[0].type === 'collection') {
        const collection = await getSharedCollectionById({ count: 60 }, files[0].resourceId);
        const collectionUris = !!collection.data && collection.data.map((item) => item.uri);
        updateDownloadRequest({
            uris: [...downloadRequest.uris, ...collectionUris],
            endpoint: ENDPOINT_ZIP,
        });
    }
    checkResource && (await validDownloadRequest({ ...downloadRequest }));
    download(downloadRequest);
};
export default downloadShare;
